// ============================================
// PAGAMENTO DA MENSALIDADE DA PLATAFORMA (Admin Master)
// ============================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack,
  Skeleton,
  Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Payment as PaymentIcon,
  ContentCopy as CopyIcon,
  ReceiptLong as ReceiptIcon,
  Refresh as RefreshIcon,
  QrCode2 as QrIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  CalendarMonth as CalendarIcon,
  EventRepeat as CycleIcon,
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#0d9488';
const ACCENT_HOVER = '#0f766e';
const HEADING = '#0f172a';

interface PlanSummary {
  enabled: boolean;
  monthlyAmountCents: number;
  dueDay: number;
  currency: string;
  nextDueDate: string | null;
  xgateConfigured: boolean;
  /** false = servidor usa e-mail do usuário logado; true = PLATFORM_BILLING_EMAIL definido */
  billingEmailConfigured?: boolean;
}

interface PaymentRow {
  id: string;
  status: string;
  amount_cents: number;
  due_date: string;
  description: string | null;
  created_at: string;
  settled_at: string | null;
  end_to_end_id: string | null;
  provider_ref: string | null;
}

interface ReceiptData {
  title: string;
  status: string;
  amountBRL: string;
  dueDate: string | null;
  paidAt: string | null;
  endToEndId: string | null;
  providerRef: string | null;
  description: string | null;
  payerHint: { name?: string; doc?: string } | null;
}

const statusColor = (
  s: string,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (s) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
    case 'cancelled':
      return 'default';
    default:
      return 'info';
  }
};

const statusLabel = (s: string) => {
  const map: Record<string, string> = {
    paid: 'Pago',
    pending: 'Pendente',
    failed: 'Falhou',
    cancelled: 'Cancelado',
    processing: 'Processando',
  };
  return map[s] || s;
};

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.6)
            : '#fff',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
          borderColor: alpha(ACCENT, 0.35),
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(ACCENT, 0.12),
              color: ACCENT,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.2, display: 'block' }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: HEADING,
                mt: 0.75,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                lineHeight: 1.25,
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PageLoadingSkeleton() {
  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="45%" height={36} sx={{ mb: 1 }} />
          <Skeleton width="70%" height={20} />
        </Box>
      </Stack>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" height={100} sx={{ borderRadius: 3, mb: 3 }} />
      <Skeleton width={200} height={28} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
    </Box>
  );
}

const PlatformPlanPayments: React.FC = () => {
  const { token } = useAuth();

  const [plan, setPlan] = useState<PlanSummary | null>(null);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [payOpen, setPayOpen] = useState(false);
  const [payData, setPayData] = useState<{
    transactionId: string;
    brcode: string;
    qrCodeImageUrl: string;
    expiresAt: string | null;
    dueDate: string;
  } | null>(null);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [pRes, listRes] = await Promise.all([
        axios.get<{ success: boolean; data: PlanSummary }>(getApiUrl('/platform-plan/plan'), {
          headers,
        }),
        axios.get<{ success: boolean; data: PaymentRow[] }>(getApiUrl('/platform-plan/payments'), {
          headers,
          params: { limit: 100 },
        }),
      ]);
      setPlan(pRes.data.data);
      setPayments(listRes.data.data || []);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data?.error
          ? String(e.response.data.error)
          : 'Não foi possível carregar os dados.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const pollPayment = useCallback(
    async (id: string) => {
      try {
        const res = await axios.get<{ success: boolean; data: PaymentRow }>(
          getApiUrl(`/platform-plan/payments/${id}`),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const st = res.data.data?.status;
        if (st === 'paid') {
          setSnack('Pagamento confirmado.');
          setPayOpen(false);
          setPayData(null);
          await loadAll();
          return true;
        }
      } catch {
        /* ignore */
      }
      return false;
    },
    [token, loadAll],
  );

  useEffect(() => {
    if (!payOpen || !payData?.transactionId) return;
    let cancelled = false;
    const id = payData.transactionId;
    const t = setInterval(async () => {
      if (cancelled) return;
      const done = await pollPayment(id);
      if (done) clearInterval(t);
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [payOpen, payData?.transactionId, pollPayment]);

  const handlePay = async () => {
    setPaying(true);
    setError(null);
    try {
      const idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? `pp-${crypto.randomUUID()}`
          : `pp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const res = await axios.post<{
        success: boolean;
        data: {
          transactionId: string;
          brcode: string;
          qrCodeImageUrl: string;
          expiresAt: string | null;
          dueDate: string;
        };
      }>(
        getApiUrl('/platform-plan/pay'),
        { idempotencyKey },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = res.data.data;
      setPayData({
        transactionId: d.transactionId,
        brcode: d.brcode,
        qrCodeImageUrl: d.qrCodeImageUrl,
        expiresAt: d.expiresAt ?? null,
        dueDate: d.dueDate,
      });
      setPayOpen(true);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data?.error
          ? String(e.response.data.error)
          : 'Erro ao gerar cobrança PIX.';
      setError(msg);
    } finally {
      setPaying(false);
    }
  };

  const openReceipt = async (id: string) => {
    try {
      const res = await axios.get<{ success: boolean; data: ReceiptData }>(
        getApiUrl(`/platform-plan/payments/${id}/receipt`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setReceipt(res.data.data);
      setReceiptOpen(true);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data?.error
          ? String(e.response.data.error)
          : 'Erro ao carregar comprovante.';
      setSnack(msg);
    }
  };

  const copyBrcode = (code: string) => {
    void navigator.clipboard.writeText(code);
    setSnack('Código PIX copiado.');
  };

  const monthlyBRL =
    plan && plan.enabled
      ? (plan.monthlyAmountCents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—';

  const paidCount = useMemo(() => payments.filter((p) => p.status === 'paid').length, [payments]);
  const pendingCount = useMemo(() => payments.filter((p) => p.status === 'pending').length, [payments]);
  const canPay = Boolean(plan?.enabled && plan?.xgateConfigured);

  if (loading) {
    return <PageLoadingSkeleton />;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'transparent',
        minHeight: '100%',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Cabeçalho da página */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: (theme) =>
            `linear-gradient(145deg, ${alpha(ACCENT, 0.07)} 0%, ${alpha('#6366f1', 0.05)} 50%, ${theme.palette.background.paper} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                bgcolor: ACCENT,
                boxShadow: `0 8px 24px ${alpha(ACCENT, 0.35)}`,
              }}
            >
              <PaymentIcon sx={{ fontSize: 30 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: HEADING,
                  fontSize: { xs: '1.35rem', sm: '1.75rem' },
                  lineHeight: 1.2,
                }}
              >
                Assinatura da plataforma
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
                Acompanhe o plano mensal, liquide via PIX e consulte o histórico com comprovantes.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {plan?.enabled ? (
                <Chip icon={<CheckCircleIcon sx={{ '&&': { fontSize: 18 } }} />} label="Plano configurado" size="small" color="success" variant="outlined" />
              ) : (
                <Chip icon={<ErrorOutlineIcon sx={{ '&&': { fontSize: 18 } }} />} label="Plano inativo" size="small" color="default" variant="outlined" />
              )}
              {plan?.enabled && plan?.xgateConfigured ? (
                <Chip icon={<QrIcon sx={{ '&&': { fontSize: 18 } }} />} label="PIX disponível" size="small" sx={{ borderColor: alpha(ACCENT, 0.5), color: ACCENT }} variant="outlined" />
              ) : plan?.enabled ? (
                <Chip label="PIX pendente" size="small" color="warning" variant="outlined" />
              ) : null}
            </Stack>
            <Tooltip title="Atualizar dados">
              <IconButton
                onClick={() => loadAll()}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!plan?.enabled && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          A cobrança do plano ainda não está ativa no servidor. Configure{' '}
          <strong>PLATFORM_PLAN_MONTHLY_CENTS</strong> (valor mínimo R$ 1,00) e{' '}
          <strong>PLATFORM_PLAN_DUE_DAY</strong> no backend.
        </Alert>
      )}

      {plan?.enabled && !plan.xgateConfigured && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          O PIX (XGate) não está configurado. Ative <strong>XGATE_ENABLED</strong> e as credenciais no
          backend para gerar cobranças.
        </Alert>
      )}

      {plan?.enabled && plan.xgateConfigured && plan.billingEmailConfigured === false && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          A cobrança PIX na XGate usa o <strong>e-mail do seu usuário</strong>. Sem e-mail no cadastro,
          o pagamento falha — defina <strong>PLATFORM_BILLING_EMAIL</strong> no servidor ou atualize seu
          perfil.
        </Alert>
      )}

      {/* Resumo em cartões */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<MoneyIcon />} label="Valor mensal" value={monthlyBRL} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<CalendarIcon />}
            label="Dia de vencimento"
            value={plan?.dueDay != null ? `Todo dia ${plan.dueDay}` : '—'}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<CycleIcon />}
            label="Próximo vencimento"
            value={
              plan?.nextDueDate
                ? new Date(plan.nextDueDate + 'T12:00:00').toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })
                : '—'
            }
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" variant="outlined" label={`${paidCount} pago(s)`} />
        {pendingCount > 0 ? (
          <Chip size="small" variant="outlined" color="warning" icon={<ScheduleIcon />} label={`${pendingCount} pendente(s)`} />
        ) : null}
        <Chip size="small" variant="outlined" label={`${payments.length} no histórico`} />
      </Stack>

      {/* Área de pagamento */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(ACCENT, 0.28),
          background: `linear-gradient(135deg, ${alpha(ACCENT, 0.08)} 0%, ${alpha('#6366f1', 0.05)} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={3}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2.5,
                bgcolor: ACCENT,
                boxShadow: `0 10px 28px ${alpha(ACCENT, 0.35)}`,
              }}
            >
              <QrIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: HEADING }}>
                Liquidar com PIX
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Gere um QR Code no valor de <strong>{monthlyBRL}</strong> para o ciclo em aberto.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            size="large"
            startIcon={paying ? <CircularProgress size={20} color="inherit" /> : <QrIcon />}
            disabled={!canPay || paying}
            onClick={handlePay}
            sx={{
              bgcolor: ACCENT,
              '&:hover': { bgcolor: ACCENT_HOVER },
              px: { xs: 2, sm: 4 },
              py: 1.35,
              borderRadius: 2,
              fontWeight: 700,
              alignSelf: { xs: 'stretch', md: 'center' },
              boxShadow: `0 8px 24px ${alpha(ACCENT, 0.35)}`,
            }}
          >
            Gerar cobrança PIX
          </Button>
        </Stack>
      </Paper>

      {/* Histórico */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: HEADING }}>
          Histórico de pagamentos
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
          {payments.length} registro{payments.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Table size="medium" sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: alpha(ACCENT, 0.08),
              }}
            >
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2, borderBottom: 'none' }}>
                Criado em
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2, borderBottom: 'none' }}>
                Vencimento
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', py: 2, borderBottom: 'none' }}>
                Valor
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2, borderBottom: 'none' }}>
                Status
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', py: 2, borderBottom: 'none' }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ border: 'none', p: 0 }}>
                  <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
                    <WalletIcon sx={{ fontSize: 52, color: 'action.disabled', mb: 1.5, opacity: 0.85 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEADING }}>
                      Nenhum pagamento no histórico
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 360, mx: 'auto' }}>
                      Quando você gerar e concluir um PIX, os lançamentos aparecerão aqui com status e comprovante.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {payments.map((row, index) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:last-child td': { borderBottom: 0 },
                  bgcolor: index % 2 === 1 ? (theme) => alpha(theme.palette.action.hover, 0.04) : 'transparent',
                }}
              >
                <TableCell sx={{ py: 2, fontWeight: 500 }}>
                  {new Date(row.created_at).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  {row.due_date
                    ? new Date(row.due_date + 'T12:00:00').toLocaleDateString('pt-BR')
                    : '—'}
                </TableCell>
                <TableCell align="right" sx={{ py: 2, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {(row.amount_cents / 100).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Chip size="small" label={statusLabel(row.status)} color={statusColor(row.status)} />
                </TableCell>
                <TableCell align="right" sx={{ py: 2 }}>
                  {row.status === 'paid' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ReceiptIcon />}
                      onClick={() => openReceipt(row.id)}
                      sx={{ borderRadius: 2, fontWeight: 600, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}
                    >
                      Comprovante
                    </Button>
                  )}
                  {row.status === 'pending' && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Aguardando PIX
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 800,
            py: 2,
            px: 2.5,
          }}
        >
          Pagamento PIX
          <IconButton onClick={() => setPayOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 3 }}>
          {payData && (
            <Stack spacing={2} alignItems="center">
              <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2, width: '100%', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                  Vencimento do ciclo
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  {new Date(payData.dueDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                </Typography>
              </Paper>
              {payData.qrCodeImageUrl && (
                <Box
                  component="img"
                  src={payData.qrCodeImageUrl}
                  alt="QR Code PIX"
                  sx={{
                    maxWidth: 260,
                    width: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.1)',
                  }}
                />
              )}
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CopyIcon />}
                onClick={() => copyBrcode(payData.brcode)}
                sx={{ borderRadius: 2, fontWeight: 600, py: 1.25 }}
              >
                Copiar código PIX (copia e cola)
              </Button>
              {payData.expiresAt && (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Expira em {new Date(payData.expiresAt).toLocaleString('pt-BR')}
                </Typography>
              )}
              <Divider flexItem sx={{ width: '100%' }} />
              <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ maxWidth: 320 }}>
                O status é atualizado automaticamente após a confirmação do pagamento.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button onClick={() => setPayOpen(false)} sx={{ fontWeight: 600 }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, py: 2, px: 2.5 }}>Comprovante de pagamento</DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 2.5 }}>
          {receipt && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {receipt.title}
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, color: ACCENT }}>
                  {receipt.amountBRL}
                </Typography>
              </Box>
              <Divider />
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip size="small" label={statusLabel(receipt.status)} color={statusColor(receipt.status)} />
                </Stack>
                {receipt.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Descrição
                    </Typography>
                    <Typography variant="body2">{receipt.description}</Typography>
                  </Box>
                )}
                {receipt.dueDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Vencimento (ciclo)
                    </Typography>
                    <Typography variant="body2">
                      {new Date(receipt.dueDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                    </Typography>
                  </Box>
                )}
                {receipt.paidAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Pago em
                    </Typography>
                    <Typography variant="body2">{new Date(receipt.paidAt).toLocaleString('pt-BR')}</Typography>
                  </Box>
                )}
                {receipt.endToEndId && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.divider, 0.06) }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      ID fim a fim (PIX)
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'ui-monospace, monospace', mt: 0.5 }}>
                      {receipt.endToEndId}
                    </Typography>
                  </Paper>
                )}
                {receipt.providerRef && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    Ref. provedor: {receipt.providerRef}
                  </Typography>
                )}
                {receipt.payerHint && (receipt.payerHint.name || receipt.payerHint.doc) && (
                  <Typography variant="body2">
                    <strong>Pagador:</strong>{' '}
                    {[receipt.payerHint.name, receipt.payerHint.doc].filter(Boolean).join(' — ')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button onClick={() => setReceiptOpen(false)} variant="contained" sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER }, fontWeight: 700, borderRadius: 2 }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default PlatformPlanPayments;
